import { useState } from "react";
import { Check, Copy } from "lucide-react";
import { Highlight } from "prism-react-renderer";
import { Button } from "~/components/ui/button";
import usePrismLanguages from "~/hooks/usePrismLanguages";
import { codeTheme } from "~/lib/codeTheme";
import { cn } from "~/lib/utils";

interface ArtifactCodeProps {
  code: string;
  // The conversations-api emits Python today, but TEXT artifacts can hold
  // anything — let callers override when they know the language.
  language?: "python" | "sql" | "json" | "bash" | "yaml";
}

// Syntax-highlighted code block with a copy button. Rendered as the
// expanded body of the toolbar's "Code" toggle in ArtifactShell. Nothing
// here knows about artifacts specifically — it's a thin wrapper around
// prism-react-renderer + clipboard.
export const ArtifactCode = ({ code, language = "python" }: ArtifactCodeProps) => {
  const { Prism } = usePrismLanguages();
  const [copied, setCopied] = useState(false);

  const onCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="relative">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onCopy}
        className="absolute right-1 top-1 h-7 px-2 text-xs text-muted-foreground"
        aria-label={copied ? "Copied" : "Copy code"}
      >
        {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
      </Button>
      <Highlight theme={codeTheme} code={code.trimEnd()} language={language} prism={Prism}>
        {({ style, tokens, getLineProps, getTokenProps }) => (
          <pre
            className={cn(
              "max-h-72 overflow-auto bg-background/80",
              "px-3 py-2 pr-10 font-mono text-[0.8125rem] leading-5"
            )}
            style={style}
          >
            {tokens.map((line, i) => (
              <div key={i} {...getLineProps({ line })}>
                {line.map((token, j) => (
                  <span key={j} {...getTokenProps({ token })} />
                ))}
              </div>
            ))}
          </pre>
        )}
      </Highlight>
    </div>
  );
};
